import { combineReducers } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/autoMergeLevel1';

import authReducer from './authSlice';
import alertReducer from './alertSlice';
import userDetailsReducer from './userDetailsSlice';
import orgReducer from './orgSlice';
import dealsReducer from './dealsSlice';
import conversationsReducer from './conversationsSlice';
import uiReducer from './uiSlice';
import stakeholdersReducer from './stakeholdersSlice';
import transcriptionReducer from './transcriptionSlice';
import artifactsReducer from './artifactsSlice';
import discoveryReducer from './discoverySlice';
import integrationsReducer from './integrationsSlice';
import emailReducer from './emailSlice';
import miscReducer from './miscSlice';
import expertBotsReducer from './expertBotsSlice';
import productTourReducer from './productTourSlice';
import dealSummaryReducer from './dealSummarySlice';
import companiesReducer from './companiesSlice';
import contactsReducer from './contactsSlice';
import notesReducer from './notesSlice';

const authPersistConfig = {
  key: 'auth',
  storage,
  whitelist: ['isLoggedIn', 'currentUser'],
};

/**
 * UI state that used to be persisted and must not be.
 *
 * The async-job request ids are only cleared by a `completed`/`failed`
 * response. An id that never got one - tab closed mid-job, or the job died
 * server-side without a terminal status - was restored from localStorage on
 * every cold load and restarted its polling loop against a job that will never
 * finish. Nothing purged them either: `persistor.purge()` is never called, so
 * logging out did not help. A job that is genuinely still running is
 * re-discovered from the server on the next visit to the deal, so keeping the
 * id across a reload buys nothing.
 */
const STALE_UI_PERSIST_KEYS = [
  'asyncDealMeetingPrep',
  'asyncMeetingPrepGoals',
  'asyncDealHighlights',
  // `emailColors` is a design token set, not a user setting: only the
  // dev-only EmailColorConfigurator can change it, and persisting it meant a
  // browser that had loaded the app once kept rendering the email rows with
  // whatever palette shipped that day, ignoring every later change to the
  // defaults. Stripping it here re-adopts `emailColors` from constants.js on
  // the next load; the configurator still applies live for the session.
  'emailColors',
];

/**
 * Drops {@link STALE_UI_PERSIST_KEYS} from the rehydrated state before merging.
 *
 * Removing them from `whitelist` alone is not enough: redux-persist applies the
 * whitelist when it *writes* (`createPersistoid`), not when it reads, so an id
 * already sitting in localStorage would still be rehydrated one last time -
 * which for a stuck job is exactly the loop being fixed. Stripping them on the
 * way in clears the residue on the first load instead.
 *
 * @param {Object} inboundState state read from storage
 * @param {Object} originalState state before rehydration
 * @param {Object} reducedState the reducer's own initial state
 * @param {Object} config the persist config
 * @returns {Object} merged state, without the stale job ids
 */
const uiStateReconciler = (
  inboundState,
  originalState,
  reducedState,
  config,
) => {
  const cleaned = { ...(inboundState || {}) };
  STALE_UI_PERSIST_KEYS.forEach((key) => delete cleaned[key]);
  return autoMergeLevel1(cleaned, originalState, reducedState, config);
};

const uiPersistConfig = {
  key: 'uiStates',
  storage,
  // `navCollapsedGroups` persists so a collapsed sidebar group stays collapsed
  // across reloads -- a nav that silently reopens every refresh reads as the
  // collapse not having worked. `twoLevelNav` is a Preferences setting, and
  // a preference that resets on reload is not one; nor is
  // `navCollapsedByDefault` or `liveTranscriptionEnabled`. `navSidebarExpanded`
  // keeps the sidebar toggle's last choice across reloads.
  whitelist: [
    'navCollapsedGroups',
    'twoLevelNav',
    'navCollapsedByDefault',
    'navSidebarExpanded',
    'liveTranscriptionEnabled',
  ],
  stateReconciler: uiStateReconciler,
};

const productTourPersistConfig = {
  key: 'productTour',
  storage,
  whitelist: ['completedTourIds', 'dismissedTourIds'],
};

const persistConfig = {
  key: 'root',
  storage,
  whitelist: [],
};

const appReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  alertState: alertReducer,
  uiStates: persistReducer(uiPersistConfig, uiReducer),
  userDetails: userDetailsReducer,
  org: orgReducer,
  deals: dealsReducer,
  transcriptionSessionToken: transcriptionReducer,
  conversations: conversationsReducer,
  stakeholders: stakeholdersReducer,
  artifacts: artifactsReducer,
  discovery: discoveryReducer,
  integrations: integrationsReducer,
  email: emailReducer,
  misc: miscReducer,
  expertBots: expertBotsReducer,
  productTour: persistReducer(productTourPersistConfig, productTourReducer),
  dealSummary: dealSummaryReducer,
  companies: companiesReducer,
  contacts: contactsReducer,
  notes: notesReducer,
});

const rootReducer = (state, action) => {
  if (action.type === 'FIREBASE_LOGOUT_SUCCESSFULL') {
    state = undefined;
  }
  return appReducer(state, action);
};

export default persistReducer(persistConfig, rootReducer);
